import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { getPublicOrigin } from "../lib/nativeBridge";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * SEOHead — injects per-route <title>, meta description, OG tags, and
 * (optional) noindex directives. Reads canonical metadata from
 * /api/seo/meta?route=<pathname> so the founder can edit copy without
 * a redeploy.
 *
 * Usage:
 *   <SEOHead />                                     // uses live route
 *   <SEOHead title="…" description="…" />           // explicit override
 *   <SEOHead noindex />                              // ad landing pages
 */
export default function SEOHead({
  title: titleOverride,
  description: descOverride,
  noindex: noindexOverride,
  image,
  jsonLd,
}) {
  const location = useLocation();
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    // If everything is explicitly overridden we don't need a fetch.
    if (titleOverride && descOverride) return;
    let cancelled = false;
    axios
      .get(`${API}/api/seo/meta`, { params: { route: location.pathname } })
      .then((r) => {
        if (!cancelled) setMeta(r.data || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [location.pathname, titleOverride, descOverride]);

  const title = titleOverride || meta?.title || "Flythouse · The Athlete Career OS";
  const description =
    descOverride ||
    meta?.description ||
    "Flythouse is the athlete career OS — where athletes, agents, brands, and programs build one connected career pipeline.";
  const noindex = noindexOverride ?? meta?.noindex ?? false;
  // Branded share-card resolution: explicit prop > per-route card from
  // /api/seo/meta > client route map (covers pages that skip the meta
  // fetch by passing explicit title/description) > site default.
  // Everything is absolutized against the live origin; data-URIs are
  // skipped (messenger crawlers can't fetch them as og:image).
  // iter307 · Use `getPublicOrigin()` — inside the Capacitor iOS shell
  // `window.location.origin` is `capacitor://localhost`, so the
  // canonical + og:image URLs used to point at a scheme no crawler /
  // Messages preview can resolve. Now they always advertise the real
  // `flythouse.com` origin regardless of runtime.
  const origin = getPublicOrigin();
  const absolutize = (u) =>
    !u || u.startsWith("data:") ? null : u.startsWith("http") ? u : `${origin}${u}`;
  const path = location.pathname || "/";
  const routeCard =
    /^\/(for-athletes|athletes|network)/.test(path) ? "/og/og-athletes.jpg"
    : /^\/(for-agents|agent)/.test(path) ? "/og/og-agents.jpg"
    : /^\/(for-brands|brand)/.test(path) ? "/og/og-brands.jpg"
    : /^\/(for-programs|programs|school)/.test(path) ? "/og/og-programs.jpg"
    : "/og/og-default.jpg";
  const ogImage =
    absolutize(image) || absolutize(meta?.og_image) || `${origin}${routeCard}`;
  const canonical = `${origin}${location.pathname}`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {meta?.keywords && <meta name="keywords" content={meta.keywords} />}
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, follow" />}
      {!noindex && <meta name="robots" content="index, follow" />}
      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Flythouse" />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      {/* iter158 · Global Organization + WebSite schema — critical for
          "Flyt House" / "Flythouse" brand SERP takeover so Google shows
          our logo + description instead of Nashville rentals or
          random architecture photos.  Injected on every page. */}
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Organization",
            "@id": "https://flythouse.com/#organization",
            "name": "Flythouse",
            "alternateName": ["Flyt House", "Flythouse Studios", "FLYT League"],
            "url": "https://flythouse.com",
            "logo": {
              "@type": "ImageObject",
              "url": "https://flythouse.com/logo-flythouse.png",
              "width": 512,
              "height": 512,
            },
            "description": "Flythouse is the athlete career OS — one connected platform for athletes, agents, brands, and school programs to run a career pipeline.  Verified athlete profiles, NIL deal flow, agent CRM, and program dashboards in one place.",
            "founder": { "@type": "Person", "name": "Jay Pernell" },
            "sameAs": [
              "https://www.instagram.com/flythouse",
              "https://www.youtube.com/@flythouse",
              "https://x.com/flythouse",
              "https://www.linkedin.com/company/flythouse",
            ],
            "contactPoint": {
              "@type": "ContactPoint",
              "email": "jay@flytstudios.com",
              "contactType": "customer support",
            },
          },
          {
            "@type": "WebSite",
            "@id": "https://flythouse.com/#website",
            "url": "https://flythouse.com",
            "name": "Flythouse",
            "publisher": { "@id": "https://flythouse.com/#organization" },
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://flythouse.com/search?q={search_term_string}",
              "query-input": "required name=search_term_string",
            },
          },
        ],
      })}</script>
      {/* JSON-LD structured data — optional per-page */}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}
